import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  MapPin, Clock, LogIn, LogOut, CheckCircle, 
  AlertCircle, Navigation, Loader2, RefreshCw, Users, Phone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MobileCheckIn = () => {
  const { token, user } = useAuth();
  
  // Also check local storage for token (persistent login) - check all possible keys
  const effectiveToken = token || 
    localStorage.getItem('suntech_mobile_token') || 
    localStorage.getItem('token');
  
  // Get user from local storage if not available from context
  const effectiveUser = user || (() => {
    try {
      const savedUser = localStorage.getItem('suntech_mobile_user') || localStorage.getItem('user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      return null;
    }
  })();

  // Check if user is Admin
  const isAdmin = effectiveUser?.role === 'Admin';
  
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [activeTab, setActiveTab] = useState('my-attendance');
  
  // Admin: Today's all attendance
  const [allAttendance, setAllAttendance] = useState(null);
  const [loadingAll, setLoadingAll] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!effectiveToken) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/attendance/my-status`, {
        headers: { Authorization: `Bearer ${effectiveToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      } else {
        console.error('Failed to fetch status:', response.status);
      }
    } catch (error) {
      console.error('Error fetching status:', error);
      toast.error('Failed to fetch attendance status');
    } finally {
      setLoading(false);
    }
  }, [effectiveToken]);

  const fetchAllAttendance = useCallback(async () => {
    if (!effectiveToken || !isAdmin) return;
    
    setLoadingAll(true);
    try {
      const response = await fetch(`${API_URL}/api/attendance/today`, {
        headers: { Authorization: `Bearer ${effectiveToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllAttendance(data);
      } else {
        console.error('Failed to fetch all attendance:', response.status);
      }
    } catch (error) {
      console.error('Error fetching all attendance:', error);
    } finally {
      setLoadingAll(false);
    }
  }, [effectiveToken, isAdmin]);

  useEffect(() => {
    fetchStatus();
    // Auto-fetch location on mount
    getLocation();
  }, [fetchStatus]);

  // Fetch all attendance when Admin switches to team tab
  useEffect(() => {
    if (activeTab === 'team-attendance' && isAdmin) {
      fetchAllAttendance();
    }
  }, [activeTab, isAdmin, fetchAllAttendance]);

  const getLocation = async () => {
    setGettingLocation(true);
    setLocationError(null);
    
    try {
      const position = await new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation not supported by your browser'));
          return;
        }
        
        navigator.geolocation.getCurrentPosition(
          resolve, 
          reject, 
          {
            enableHighAccuracy: true,
            timeout: 20000,
            maximumAge: 0
          }
        );
      });
      
      const coords = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: Math.round(position.coords.accuracy)
      };
      
      setLocation(coords);
      setLocationError(null);
      return coords;
    } catch (error) {
      let message = 'Unable to get location';
      if (error.code === 1) {
        message = 'Location permission denied. Please enable location access in your browser settings.';
      } else if (error.code === 2) {
        message = 'Location unavailable. Please try again.';
      } else if (error.code === 3) {
        message = 'Location request timed out. Please try again.';
      }
      setLocationError(message);
      console.error('Location error:', error);
      return null;
    } finally {
      setGettingLocation(false);
    }
  };

  const handleCheckIn = async () => {
    if (!effectiveToken) {
      toast.error('Session expired. Please login again.');
      return;
    }
    
    setActionLoading(true);
    
    try {
      // Get fresh location
      let coords = location;
      if (!coords) {
        coords = await getLocation();
      }
      
      if (!coords) {
        toast.error('Location is required for check-in. Please enable location access.');
        setActionLoading(false);
        return;
      }
      
      const url = `${API_URL}/api/attendance/check-in?latitude=${coords.latitude}&longitude=${coords.longitude}&accuracy=${coords.accuracy || 0}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${effectiveToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Check-in successful!');
        fetchStatus();
      } else {
        const error = await response.json();
        console.error('Check-in error response:', error);
        toast.error(error.detail || 'Check-in failed');
      }
    } catch (error) {
      console.error('Check-in error:', error);
      toast.error('Check-in failed. Please check your connection and try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!effectiveToken) {
      toast.error('Session expired. Please login again.');
      return;
    }
    
    setActionLoading(true);
    
    try {
      // Get fresh location
      let coords = location;
      if (!coords) {
        coords = await getLocation();
      }
      
      if (!coords) {
        toast.error('Location is required for check-out. Please enable location access.');
        setActionLoading(false);
        return;
      }
      
      const url = `${API_URL}/api/attendance/check-out?latitude=${coords.latitude}&longitude=${coords.longitude}&accuracy=${coords.accuracy || 0}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${effectiveToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Check-out successful! Total: ${data.total_hours?.toFixed(2) || 0} hours`);
        fetchStatus();
      } else {
        const error = await response.json();
        console.error('Check-out error response:', error);
        toast.error(error.detail || 'Check-out failed');
      }
    } catch (error) {
      console.error('Check-out error:', error);
      toast.error('Check-out failed. Please check your connection and try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const formatTime = (time) => {
    if (!time) return '--:--';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${displayHour}:${minutes} ${suffix}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const isCheckedIn = status?.is_checked_in;
  const activeRecord = status?.active_record;
  const todayRecords = status?.records || [];

  // Helper to format time
  const formatTimeAMPM = (time) => {
    if (!time) return '--:--';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${displayHour}:${minutes} ${suffix}`;
  };

  // My Attendance Content
  const MyAttendanceContent = () => (
    <div className="space-y-4">
      {/* Status Card */}
      <Card className={`border-0 shadow-lg ${isCheckedIn ? 'bg-gradient-to-br from-emerald-500 to-emerald-600' : 'bg-gradient-to-br from-slate-600 to-slate-700'}`}>
        <CardContent className="p-6">
          <div className="text-center text-white">
            <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${isCheckedIn ? 'bg-white/20' : 'bg-white/10'}`}>
              {isCheckedIn ? (
                <CheckCircle className="w-10 h-10" />
              ) : (
                <Clock className="w-10 h-10" />
              )}
            </div>
            <h2 className="text-xl font-bold mb-1">
              {isCheckedIn ? 'You are Checked In' : 'Not Checked In'}
            </h2>
            {isCheckedIn && activeRecord && (
              <p className="opacity-90">
                Since {formatTime(activeRecord.check_in_time)}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Location Status */}
      <Card className="border-0 shadow-md bg-white">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${location ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                <MapPin className={`w-5 h-5 ${location ? 'text-emerald-600' : 'text-slate-400'}`} />
              </div>
              <div>
                <p className="font-medium text-slate-700">Location</p>
                {location ? (
                  <p className="text-xs text-emerald-600">
                    ✓ Location acquired (±{location.accuracy}m)
                  </p>
                ) : locationError ? (
                  <p className="text-xs text-red-500">{locationError}</p>
                ) : (
                  <p className="text-xs text-slate-500">Tap refresh to get location</p>
                )}
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={getLocation}
              disabled={gettingLocation}
              className="border-slate-200"
            >
              {gettingLocation ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Action Button */}
      <Button
        className={`w-full h-16 text-lg font-semibold shadow-lg ${
          isCheckedIn 
            ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-red-500/30' 
            : 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 shadow-emerald-500/30'
        }`}
        onClick={isCheckedIn ? handleCheckOut : handleCheckIn}
        disabled={actionLoading || (!location && gettingLocation)}
      >
        {actionLoading ? (
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
        ) : isCheckedIn ? (
          <LogOut className="w-6 h-6 mr-2" />
        ) : (
          <LogIn className="w-6 h-6 mr-2" />
        )}
        {actionLoading ? 'Processing...' : isCheckedIn ? 'CHECK OUT' : 'CHECK IN'}
      </Button>

      {!location && !gettingLocation && (
        <p className="text-center text-sm text-orange-600">
          ⚠️ Please enable location to check in/out
        </p>
      )}

      {/* Today's Records */}
      {todayRecords.length > 0 && (
        <Card className="border-0 shadow-md bg-white">
          <CardContent className="p-4">
            <h3 className="font-semibold text-slate-700 mb-3">Today&apos;s Records</h3>
            <div className="space-y-3">
              {todayRecords.map((record, idx) => (
                <div key={idx} className="flex justify-between items-center py-3 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      In: {formatTime(record.check_in_time)}
                    </Badge>
                    {record.check_out_time && (
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                        Out: {formatTime(record.check_out_time)}
                      </Badge>
                    )}
                  </div>
                  {record.total_hours > 0 && (
                    <span className="text-sm font-medium text-slate-600">
                      {record.total_hours.toFixed(2)} hrs
                    </span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {todayRecords.length === 0 && (
        <Card className="border-0 shadow-md bg-white">
          <CardContent className="p-6 text-center text-slate-500">
            <Clock className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p>No attendance records for today</p>
            <p className="text-sm text-slate-400 mt-1">Check in to start tracking</p>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Team Attendance Content (Admin only)
  const TeamAttendanceContent = () => {
    if (loadingAll) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      );
    }

    if (!allAttendance) {
      return (
        <Card className="border-0 shadow-md bg-white">
          <CardContent className="p-6 text-center text-slate-500">
            <Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p>Failed to load team attendance</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={fetchAllAttendance}>
              <RefreshCw className="w-4 h-4 mr-2" /> Retry
            </Button>
          </CardContent>
        </Card>
      );
    }

    const { summary, attendance_records, absent, on_leave } = allAttendance;

    return (
      <div className="space-y-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="border-0 shadow-md bg-emerald-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-emerald-700">{summary?.present || 0}</p>
              <p className="text-xs text-emerald-600">Present Today</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-blue-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-700">{summary?.currently_checked_in || 0}</p>
              <p className="text-xs text-blue-600">Currently In</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-orange-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-orange-700">{summary?.on_leave || 0}</p>
              <p className="text-xs text-orange-600">On Leave</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md bg-red-50">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-700">{summary?.absent || 0}</p>
              <p className="text-xs text-red-600">Absent</p>
            </CardContent>
          </Card>
        </div>

        {/* Checked In Employees */}
        <Card className="border-0 shadow-md bg-white">
          <CardContent className="p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-slate-700">Checked In Today</h3>
              <Button variant="ghost" size="sm" onClick={fetchAllAttendance}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
            {attendance_records?.length > 0 ? (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {attendance_records.map((record, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div className="flex-1">
                      <p className="font-medium text-sm text-slate-700">{record.employee?.name || 'Unknown'}</p>
                      <p className="text-xs text-slate-500">{record.employee?.role}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 text-xs">
                        In: {formatTimeAMPM(record.check_in_time)}
                      </Badge>
                      {record.check_out_time ? (
                        <Badge variant="outline" className="ml-1 bg-red-50 text-red-700 text-xs">
                          Out: {formatTimeAMPM(record.check_out_time)}
                        </Badge>
                      ) : (
                        <Badge className="ml-1 bg-emerald-500 text-white text-xs">Working</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">No check-ins yet today</p>
            )}
          </CardContent>
        </Card>

        {/* Absent Employees */}
        {absent?.length > 0 && (
          <Card className="border-0 shadow-md bg-white">
            <CardContent className="p-4">
              <h3 className="font-semibold text-red-600 mb-3">Not Checked In ({absent.length})</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {absent.map((emp, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div>
                      <p className="font-medium text-sm text-slate-700">{emp.name}</p>
                      <p className="text-xs text-slate-500">{emp.role}</p>
                    </div>
                    <Badge variant="outline" className="bg-red-50 text-red-600 text-xs">Absent</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* On Leave */}
        {on_leave?.length > 0 && (
          <Card className="border-0 shadow-md bg-white">
            <CardContent className="p-4">
              <h3 className="font-semibold text-orange-600 mb-3">On Leave ({on_leave.length})</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {on_leave.map((emp, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div>
                      <p className="font-medium text-sm text-slate-700">{emp.name}</p>
                      <p className="text-xs text-slate-500">{emp.role}</p>
                    </div>
                    <Badge variant="outline" className="bg-orange-50 text-orange-600 text-xs">Leave</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 pb-24">
      {isAdmin ? (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="my-attendance">My Attendance</TabsTrigger>
            <TabsTrigger value="team-attendance">Team ({allAttendance?.summary?.total_employees || '-'})</TabsTrigger>
          </TabsList>
          <TabsContent value="my-attendance">
            <MyAttendanceContent />
          </TabsContent>
          <TabsContent value="team-attendance">
            <TeamAttendanceContent />
          </TabsContent>
        </Tabs>
      ) : (
        <MyAttendanceContent />
      )}
    </div>
  );
};

export default MobileCheckIn;
